import { Box } from "@chakra-ui/react";
import type { GridReadyEvent, SelectionChangedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { forwardRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

interface PreconnectTableProps {
  entries: any[];
  onSelectionChanged: (selectedRows: any[]) => void;
  onToggleStatus: (id: string, currentStatus: string) => void;
  loadingEntries: Set<string>;
}

export const PreconnectTable = forwardRef<AgGridReact, PreconnectTableProps>(
  ({ entries, onSelectionChanged, onToggleStatus, loadingEntries }, ref) => {
    const navigate = useNavigate();

    const columnDefs = useMemo(
      () => [
        {
          field: "url",
          headerName: "URL",
          flex: 2,
          minWidth: 250,
          cellRenderer: (params: any) => {
            return (
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span>{params.value}</span>
              </div>
            );
          }
        },
        {
          field: "type",
          headerName: "Type",
          width: 130,
          cellRenderer: (params: any) => {
            const type = params.value;
            const bgColor = type === "preconnect" ? "#E9D8FD" : "#BEE3F8";
            const textColor = type === "preconnect" ? "#6B46C1" : "#2C5282";
            return (
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: "4px",
                  backgroundColor: bgColor,
                  color: textColor,
                  fontSize: "12px",
                  fontWeight: 500
                }}
              >
                {type}
              </span>
            );
          }
        },
        {
          field: "description",
          headerName: "Description",
          flex: 2,
          minWidth: 200
        },
        {
          field: "paths",
          headerName: "Applied To",
          flex: 1.5,
          minWidth: 150,
          cellRenderer: (params: any) => {
            const paths = params.value || [];
            if (paths.length === 0) return "No paths";
            if (paths[0] === "*") return "All paths";
            if (paths.length === 1) return paths[0];
            return `${paths.length} paths`;
          }
        },
        {
          field: "usageCount",
          headerName: "Usage",
          width: 120,
          cellRenderer: (params: any) => {
            return params.value ? params.value.toLocaleString() : "0";
          }
        },
        {
          field: "performance.avgTimeSaved",
          headerName: "Avg Time Saved",
          width: 140,
          cellRenderer: (params: any) => {
            const time = params.data?.performance?.avgTimeSaved;
            if (!time) return "-";
            return `${time}ms`;
          }
        },
        {
          field: "performance.successRate",
          headerName: "Success Rate",
          width: 120,
          cellRenderer: (params: any) => {
            const rate = params.data?.performance?.successRate;
            if (!rate) return "-";
            const color = rate >= 98 ? "#48BB78" : rate >= 95 ? "#ED8936" : "#F56565";
            return (
              <span style={{ color, fontWeight: 500 }}>
                {rate.toFixed(1)}%
              </span>
            );
          }
        },
        {
          field: "status",
          headerName: "Status",
          width: 100,
          cellRenderer: (params: any) => {
            const { id, status } = params.data;
            const isActive = status === "active";
            const isLoading = loadingEntries.has(id);

            return (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleStatus(id, status);
                }}
                disabled={isLoading}
                style={{
                  padding: "4px 12px",
                  borderRadius: "6px",
                  border: "none",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: isLoading ? "not-allowed" : "pointer",
                  backgroundColor: isActive ? "#C6F6D5" : "#FED7D7",
                  color: isActive ? "#22543D" : "#742A2A",
                  opacity: isLoading ? 0.6 : 1,
                  transition: "all 0.2s"
                }}
              >
                {isLoading ? "..." : isActive ? "Active" : "Inactive"}
              </button>
            );
          }
        },
        {
          field: "lastUsed",
          headerName: "Last Used",
          width: 150,
          cellRenderer: (params: any) => {
            if (!params.value) return "Never";
            const date = new Date(params.value);
            const now = new Date();
            const diffMs = now.getTime() - date.getTime();
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 60) return `${diffMins}m ago`;
            if (diffHours < 24) return `${diffHours}h ago`;
            if (diffDays < 7) return `${diffDays}d ago`;
            return date.toLocaleDateString();
          }
        }
      ],
      [onToggleStatus, loadingEntries]
    );

    const defaultColDef = useMemo(
      () => ({
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: false
      }),
      []
    );

    const onGridReady = useCallback((event: GridReadyEvent) => {
      event.api.autoSizeAllColumns();
    }, []);

    const handleSelectionChanged = useCallback(
      (event: SelectionChangedEvent) => {
        const selectedNodes = event.api.getSelectedNodes();
        const selectedData = selectedNodes.map((node) => node.data);
        onSelectionChanged(selectedData);
      },
      [onSelectionChanged]
    );

    return (
      <Box className="ag-theme-alpine" height="600px">
        <AgGridReact
          ref={ref}
          rowData={entries}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          onSelectionChanged={handleSelectionChanged}
          onRowDoubleClicked={(params) => {
            if (params.data?.id) {
              navigate(`/website/preconnect/${params.data.id}/edit`);
            }
          }}
          getRowStyle={() => ({
            cursor: "pointer"
          })}
          onFirstDataRendered={(params) => {
            params.api.autoSizeAllColumns();
          }}
          onGridSizeChanged={(params) => {
            params.api.autoSizeAllColumns();
          }}
          animateRows={true}
          enableCharts={true}
          sideBar={{
            toolPanels: [
              {
                id: "columns",
                labelDefault: "Columns",
                labelKey: "columns",
                iconKey: "columns",
                toolPanel: "agColumnsToolPanel"
              },
              {
                id: "filters",
                labelDefault: "Filters",
                labelKey: "filters",
                iconKey: "filter",
                toolPanel: "agFiltersToolPanel"
              }
            ],
            hiddenByDefault: true
          }}
          rowSelection={{
            mode: "multiRow",
            checkboxes: true,
            enableClickSelection: false,
            headerCheckbox: true
          }}
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={[5, 10, 20, 50]}
          paginationAutoPageSize={false}
          suppressPaginationPanel={false}
          paginationNumberFormatter={(params) => {
            return params.value.toLocaleString();
          }}
          localeText={{
            page: "Page",
            more: "More",
            to: "to",
            of: "of",
            next: "Next",
            last: "Last",
            first: "First",
            previous: "Previous",
            loadingOoo: "Loading...",
            noRowsToShow: "No preconnect entries to show"
          }}
          rowHeight={35}
          loading={false}
        />
      </Box>
    );
  }
);